import React from "react";
import {
    UserMinusIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ChartBarIcon,
    PlusIcon,
    EyeIcon,
} from "@heroicons/react/24/outline";
import { DataTable } from "../../common/DataTable";

const RelievedEmployeeTab = ({
    dashboardData,
    setModalMode,
    resetRelievedForm,
    setShowRelievedModal,
    relievedEmployees,
    getClearanceStatusColor,
    setSelectedRecord,
    setRelievedForm,
    relievedPagination,
    loadRelievedEmployees,
    loading,
}) => {
    const columns = [
        {
            id: "employee",
            header: "Employee",
            cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.employee_name}</span>,
        },
        { accessorKey: "department", header: "Department" },
        { accessorKey: "lwd", header: "LWD" },
        {
            accessorKey: "clearance_status",
            header: "Clearance Status",
            cell: ({ getValue }) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getClearanceStatusColor(getValue())}`}>
                    {getValue().replace("_", " ").toUpperCase()}
                </span>
            ),
        },
        {
            accessorKey: "reason_for_leaving",
            header: "Exit Details",
        },
        {
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    onClick={() => {
                        setSelectedRecord(row.original);
                        setRelievedForm(row.original);
                        setModalMode("edit");
                        setShowRelievedModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900"
                >
                    <EyeIcon className="w-4 h-4 mr-1" />
                </button>
            ),
        },
    ];

    return (
    <div className="space-y-6">
        {dashboardData && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <UserMinusIcon className="w-8 h-8 text-red-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Relieved</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.relieved_summary?.total_relieved || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Clearances Completed</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.relieved_summary?.clearances_completed || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Clearances Pending</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.relieved_summary?.clearances_pending || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <ChartBarIcon className="w-8 h-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.relieved_summary?.completion_rate || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>
        )}

        <div className="flex justify-end">
            <button
                onClick={() => {
                    setModalMode("create");
                    resetRelievedForm();
                    setShowRelievedModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-medium text-white hover:bg-red-700"
            >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Relieved Employee
            </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Relieved Employee Records</h3>
                <p className="text-sm text-gray-600 mt-1">Track employee exit process and clearance status</p>
            </div>
            <DataTable
                columns={columns}
                data={relievedEmployees}
                getRowId={(employee) => String(employee.employee_id)}
                loading={loading}
                emptyMessage="No relieved employees found"
                manualPagination
                rowCount={relievedPagination.totalCount || 0}
                pagination={{ pageIndex: (relievedPagination.currentPage || 1) - 1, pageSize: relievedPagination.pageSize }}
                onPaginationChange={(updater) => {
                    const current = { pageIndex: (relievedPagination.currentPage || 1) - 1, pageSize: relievedPagination.pageSize };
                    const next = typeof updater === "function" ? updater(current) : updater;
                    loadRelievedEmployees(next.pageIndex + 1, next.pageSize);
                }}
                enableColumnVisibility={false}
                enableColumnResizing
                maxBodyHeight="65vh"
            />
        </div>
    </div>
    );
};

export default RelievedEmployeeTab;
